/** Copyright (c) 2018 T.Rowe Price. All rights reserved. */

import { filter, flow, groupBy, invert, reduce } from 'lodash';

import { ownershipMapping, ownershipTypes } from 'services/TableSupport';
import {
  ALL_LABEL,
  APP_LABEL,
  BROKERAGE_LABEL,
  COLLEGE_SAVINGS_LABEL,
  AK_ONLY_COLLEGE_SAVINGS_LABEL,
  CUSTOM_LABEL,
  MUTUAL_FUND_LABEL,
  NON_RETIREMENT_LABEL,
  OWNED_LABEL,
  RAS_LABEL,
  RETIREMENT_LABEL,
  VIEW_ONLY_LABEL
} from './Filters';
import {hasBrokerageAdvantage} from "./Accounts";
import { enableBeneBrokerageAdvantage } from "./FeatureFlags";

export const MUTUAL_FUND = 10;
export const BROKERAGE = 20;
export const MARYLAND_529 = 40;
export const TRAC_PLAN = 50;
export const OMNI_PLAN = 60;
export const ALASKA_529 = 70;
export const NATIONAL_529 = 80;
export const ACTIVE_PLUS = 110;
export const ACTIVE_ADVISORY = 120;


export const EXIT_MODAL_VIEW_COUNT = 'exitModalViewCount';

//This maps to the RSP ENUM for Product Type
export const PRODUCTS = {
  10: 'MutualFund',
  20: 'Brokerage',
  40: 'Maryland529',
  50: 'WorkplaceRetirement',
  60: 'RetirementPlan',
  70: 'Alaska529',
  80: 'National529',
  110: 'ManagedAccount',
  120: 'AdviceManaged'
};

export const productMap = {
  activePlus: ACTIVE_PLUS,
  activeAdvisory: ACTIVE_ADVISORY,
  alaska529: ALASKA_529,
  brokerage: BROKERAGE,
  maryland529: MARYLAND_529,
  mutualFund: MUTUAL_FUND,
  national529: NATIONAL_529,
  omniPlan: OMNI_PLAN,
  tracPlan: TRAC_PLAN
};

export const productTypePriority = [
  'activeAdvisory',
  'mutualFund',
  'activePlus',
  'brokerage',
  'national529',
  'maryland529',
  'alaska529',
  'omniPlan',
  'tracPlan'
];

export const ACTIVE_ADVISORY_CLASS = 'activeAdvisory';
export const ADVICE_MANAGED_CLASS = 'adviceManaged';
export const ACTIVE_PLUS_CLASS = 'activePlus';
export const MUTUAL_FUND_CLASS = 'mutualFund';
export const BROKERAGE_CLASS = 'brokerage';
export const COLLEGE_SAVINGS_CLASS = 'collegeSavings';
export const ALASKA_529_CLASS = 'alaska529';
export const MARYLAND_529_CLASS = 'maryland529';
export const MD_529_CLASS = 'md529';
export const NATIONAL_529_CLASS = 'national529';
export const NA_529_CLASS = 'na529';
export const RETIREMENT_CLASS = 'retirement';
export const NON_RETIREMENT_CLASS = 'nonRetirement';
export const OWNED_CLASS = 'owned';
export const VIEW_ONLY_CLASS = 'viewOnly';

// brokerage asset types
export const MUTUAL_FUND_ASSET = 'MUTUAL_FUND';
export const EQUITY_ASSET  = 'EQUITY';

// taxonomy category & account group tier types
export const CATEGORY = 'CATEGORY';
export const RETAIL = 'RETAIL';
export const ADVISORY = 'ADVISORY';
export const SMALL_BUSINESS_RETIREMENT = 'SMALL_BUSINESS_RETIREMENT';


export const getProvidedProductType = (type) => {
  if (type === NATIONAL_529_CLASS || type === NAT_CLASS) {
    return NATIONAL_529_CLASS;
  } else if (type === ALASKA_529_CLASS || type === AK_CLASS) {
    return ALASKA_529_CLASS;
  } else if (type === MARYLAND_529_CLASS || type === MD_CLASS) {
    return MARYLAND_529_CLASS;
  }

  return MUTUAL_FUND_CLASS;
};

// accounts/performance/product call uses nonstandard adviceManaged class for RAS products
export const isAdviceProductClass = (productClass) =>
  productClass === ACTIVE_ADVISORY_CLASS ||
  productClass === ADVICE_MANAGED_CLASS;

export const isActivePlusProductClass = (productClass) =>
  productClass === ACTIVE_PLUS_CLASS;

export const isPershingAccount = (productID) =>{
  return productID === ACTIVE_PLUS || productID === ACTIVE_ADVISORY || productID === BROKERAGE;
}

export const isCollegeSavingsProductClass = (productClass) => {
  return (
    productClass === COLLEGE_SAVINGS_CLASS ||
    productClass === ALASKA_529_CLASS ||
    productClass === MARYLAND_529_CLASS ||
    productClass === NATIONAL_529_CLASS
  );
};

export const productMapByNumber = invert(productMap);

export const isActiveAdvisory = (type) => type === ACTIVE_ADVISORY;
export const isActivePlus = (type) => type === ACTIVE_PLUS;
export const isAlaska529 = (type) => type === ALASKA_529;
export const isBrokerage = (type) => type === BROKERAGE;
export const isBrokerageOrActivePlus = (type) =>
  isBrokerage(type) || isActivePlus(type);
export const isBrokerageOrActiveAdvisory = (type) =>
  isBrokerage(type) || isActiveAdvisory(type);
export const isActiveAdvisoryOrActivePlus = (type) =>
  isActiveAdvisory(type) || isActivePlus(type);
export const isPershingProduct = (type) =>
  isBrokerage(type) || isActivePlus(type) || isActiveAdvisory(type);
export const isCollegeSavings = (type) =>
  isMaryland529(type) || isAlaska529(type) || isNational529(type);
export const isMaryland529 = (type) => type === MARYLAND_529;
export const isMutualFund = (type) => type === MUTUAL_FUND;
export const isNational529 = (type) => type === NATIONAL_529;
export const isOmniPlan = (type) => type === OMNI_PLAN;
export const isTracPlan = (type) => type === TRAC_PLAN;
export const isRetailTrac = (account) => {
  const { isRetailTrac = false } = account;
  return isRetailTrac;
}
export const isWorkplaceRetirement = (type) =>
  isOmniPlan(type) || isTracPlan(type);
export const isManaged = (type) => isActiveAdvisory(type);
export const isSelfManaged = (type) => !isActiveAdvisory(type);

export const productTypeNameFromNumber = (productType) =>
  isCollegeSavings(productType)
    ? 'collegeSavingsPlan'
    : productMapByNumber[productType];

export const isViewOnlyAccount = (account) =>
  ownershipMapping(account) === ownershipTypes.VIEW_ONLY;

export const isRelatedAccount = (account) =>
  ownershipMapping(account) === ownershipTypes.RELATED;

export const isRetirementAccount = (account) =>
  account && account.retirementAccount;

export const isAllViewOnlyAccounts = (accounts) =>
  accounts.every((a) => a && a.isViewOnly);

export const isOwnedAccount = (account) =>
  account ? ownershipMapping(account) === ownershipTypes.OWNED : false;

export const hasOwnedAccounts = (accounts) =>
  accounts.some((a) => ownershipMapping(a) === ownershipTypes.OWNED);

export const isTaxesEligible = (account) => account && !isViewOnlyAccount(account) && !isRetirementAccount(account)

export const filterAccountsOnProductClass = (
  accounts,
  productClass,
  ignorePClass = false
) => {
  return accounts.filter((a) =>
    !ignorePClass
      ? a.productClass === productClass
      : a.productClass !== productClass
  );
};


/**
 * Returns the array of product classes dependent on the selected filter value.
 * @param selectedValueFilter
 * @returns {(string)[]}
 */
export const getSelectedProductClasses = (selectedValueFilter) => {
  switch (selectedValueFilter) {
    case RAS_LABEL: {
      return [ACTIVE_ADVISORY_CLASS];
    }
    case APP_LABEL: {
      return [ACTIVE_PLUS_CLASS];
    }
    case MUTUAL_FUND_LABEL: {
      return [MUTUAL_FUND_CLASS];
    }
    case BROKERAGE_LABEL: {
      return [BROKERAGE_CLASS];
    }
    case COLLEGE_SAVINGS_LABEL: {
      return [COLLEGE_SAVINGS_CLASS];
    }
    case AK_ONLY_COLLEGE_SAVINGS_LABEL: {
      return [COLLEGE_SAVINGS_CLASS];
    }
    case ALL_LABEL:
    case RETIREMENT_LABEL:
    case NON_RETIREMENT_LABEL:
    case VIEW_ONLY_LABEL:
    case CUSTOM_LABEL:
    case OWNED_LABEL: {
      return [
        ACTIVE_ADVISORY_CLASS,
        ACTIVE_PLUS_CLASS,
        MUTUAL_FUND_CLASS,
        BROKERAGE_CLASS,
        COLLEGE_SAVINGS_CLASS
      ];
    }
    default:
      return [];
  }
};

/**
 * Reduce product types to determine those which are view
 * only for all 529 accounts of each type.
 * @param {Object[]} accounts
 * @returns {Object.<string,boolean>}
 */
export const collegeSavingsViewOnlyMap = (accounts) => {
  // filter 529 accounts types: productType -> [ account, ... ]
  const viewOnly529s = flow(
    (acc) => filter(acc, (a) => isCollegeSavings(a.productType)),
    (acc) => groupBy(acc, (a) => a.productType)
  )(accounts);
  // reduce to: productType -> isAllViewOnlyAccounts (true/false)
  return reduce(
    viewOnly529s,
    (result, value, key) => ({
      ...result,
      [productMapByNumber[key]]: isAllViewOnlyAccounts(value)
    }),
    {}
  );
};

export const getSweepAccountIds = (accounts) =>
  accounts.reduce((sweepAccounts, account) => {
    if (account.sweepAccount) {
      sweepAccounts.push(account.accountId);
    }
    return sweepAccounts;
  }, []);

export const productTypes = {
  mutualFund: {
    label: 'Mutual Funds',
    balance: 0.0,
    percent: 0.0,
    userAccountNumbers: []
  },
  brokerage: {
    label: 'Brokerage',
    balance: 0.0,
    percent: 0.0,
    userAccountNumbers: []
  },
  collegeSavings: {
    label: '529 College Savings Plans', // note: label may be overridden in products reducer
    balance: 0.0,
    percent: 0.0,
    userAccountNumbers: []
  },
  activePlus: {
    label: 'ActivePlus Portfolios®',
    balance: 0.0,
    percent: 0.0,
    userAccountNumbers: []
  },
  activeAdvisory: {
    label: 'Retirement Advisory Service™',
    balance: 0.0,
    percent: 0.0,
    userAccountNumbers: []
  }
};

Object.keys(productTypes).forEach((k) => (productTypes[k].key = k));

export const products = {
  types: productTypes,
  byAccount: {
    10: productTypes.mutualFund,
    20: productTypes.brokerage,
    40: productTypes.collegeSavings,
    70: productTypes.collegeSavings,
    80: productTypes.collegeSavings,
    110: productTypes.activePlus,
    120: productTypes.activeAdvisory
  },
  providers: productMap
};

export const getProductTypeClass = (productType) => {
  return productMapByNumber[productType];
};

export const getProductName = (productType) => {
  const productClass =  getProductTypeClass(productType);
  return productTypes[productClass]?.label || '';
}

export const MD_CLASS = 'md529';
export const AK_CLASS = 'alaska529'; // this was needed until CLX can support ak529 - TargetDate Nov 2021
export const NAT_CLASS = 'na529';
export const RETAIL_CLASS = 'retail';
export const RETAIL_CLASS_WITH_ACTIVE_PLUS = 'retailwApp';
export const RETAIL_CLASS_WITH_BROKERAGE = 'retailwBrk';

export const getProductClass = (view) => {
  const {
    hasAlaska529Only,
    hasActivePlus,
    hasBrokerage,
    hasMaryland529Only,
    hasNational529Only
  } = view;
  return hasMaryland529Only
    ? MD_CLASS
    : hasNational529Only
    ? NAT_CLASS
    : hasAlaska529Only
    ? AK_CLASS
    : hasBrokerage
    ? RETAIL_CLASS_WITH_BROKERAGE
    : hasActivePlus
    ? RETAIL_CLASS_WITH_ACTIVE_PLUS
    : RETAIL_CLASS;
};

export const getProducts = (accounts) => {
  return accounts.map((a) => a.productType);
};

export const BENEFICIARY_ELIGIBLE_PRODUCTS = [
  MUTUAL_FUND,
  ACTIVE_PLUS,
  ACTIVE_ADVISORY
];

export const isProductEligibleForBeneficiary = (account) => {
  return (enableBeneBrokerageAdvantage() && account.productType === BROKERAGE && hasBrokerageAdvantage(account.serviceIds))
    || BENEFICIARY_ELIGIBLE_PRODUCTS.includes(account.productType);
};

export const AS_OF_DATE_VISIBILITY_MAP = [
  BROKERAGE,
  ACTIVE_PLUS,
  ACTIVE_ADVISORY
];

export const getAccountLevelAsOfDateVisible = (productType) => {
  return AS_OF_DATE_VISIBILITY_MAP.includes(productType);
};
